import { SUPPORTED_LOCALES, type SupportedLocale } from '@diario/common';
import { useTranslation } from 'react-i18next';
import { CLASE_BASE } from './input';

/**
 * H-21: antes este `<select>` duplicaba a mano el borde/radio/foco/transición de `CLASE_BASE`
 * (`Input`/`Textarea`/`Select`, `components/ui/input.tsx`). Se reutiliza aquí filtrando las
 * cuatro utilidades que este control necesita distintas por breakpoint (`w-full`, `px-3`,
 * `text-base`, `text-ink` — ver JSDoc de más abajo, el truco del código superpuesto): dejarlas
 * puestas competiría por concatenación con el `w-12`/`px-2`/`text-transparent` propios de aquí,
 * con la misma especificidad y un ganador dependiente del orden de la hoja compilada
 * (`docs/design-system.md` § La regla del fondo oscuro). El resto de `CLASE_BASE` (borde, radio,
 * fondo, halo de foco, transición) sí se comparte tal cual — mismo resultado visual que antes.
 */
const CAMPOS_PROPIOS = new Set(['px-3', 'text-base', 'text-ink', 'w-full']);
const CLASE_BASE_SIN_TAMANO = CLASE_BASE.split(' ')
	.filter(clase => !CAMPOS_PROPIOS.has(clase))
	.join(' ');

/**
 * Conmutador de idioma. Componente compartido: toda pieza de `components/ui` lleva su
 * fichero `.stories.tsx` (Definition of Done).
 *
 * Pensado para vivir en la cabecera junto a un menú (T-2.2): el `<select>` lleva el aspecto de
 * campo del sistema sobre tokens (fondo `paper`, borde `border`, radio 8 px, texto `ink`, altura
 * ≥ 44 px) y conserva la flecha nativa del navegador — sin `appearance-none` ni icono propio, para
 * no perder la interacción nativa del control.
 *
 * Foco (P-53, respuesta del cliente 17/09/2026 — «también en los campos y desplegables»): borde
 * `accent` + el contorno sólido de tinta `outline-ink` que ya usan botones y menús, heredado de
 * `CLASE_BASE` (`components/ui/input.tsx`) sin filtrar — este componente solo quita de esa clase
 * base las cuatro utilidades de tamaño que necesita distintas (`CAMPOS_PROPIOS`, ver JSDoc de más
 * abajo), nunca las de foco. Ya no es el halo `focus-ring` al 20%: ese token sigue existiendo en
 * `tokens.css` para sus otros usos, solo deja de aplicarse en este `<select>`.
 *
 * En `<sm` la etiqueta «Idioma»/«Language» pasa a `sr-only` (H-05): el `<select>` sigue
 * accesible por su nombre —`sr-only` no lo saca del árbol de accesibilidad, solo lo oculta
 * visualmente y lo excluye del flujo flex (`position: absolute`), así que deja de empujar el
 * ancho de la cabecera— y vuelve a pintarse a partir de `sm:` (`sm:not-sr-only`).
 *
 * Además, en `<sm` el propio `<select>` muestra el código (`ES`/`EN`) en vez del nombre completo
 * (H-05b, cierre de T-5): un `<select>` nativo siempre pinta el texto de su `<option>`
 * seleccionada, así que no hay forma de que muestre otra cosa sin dejar de ser un único control
 * accesible. La técnica elegida — en vez de `appearance-none` + icono propio, que perdería la
 * flecha nativa que el JSDoc de arriba pide conservar — es pintar el `<select>` con
 * `text-transparent` (se queda angosto, `w-12`, solo para la flecha) y superponerle un
 * `<span aria-hidden='true'>` con el código que ocupa visualmente el hueco del texto: el nombre
 * accesible (por el `<label>`) y las `<option>` nunca cambian — siguen siendo «Español»/«Spanish».
 * El `<span>` del código vive **fuera** del `<label>` (hermano suyo, dentro del contenedor
 * `relative` que también posiciona el propio `<label>`), no dentro: `aria-hidden='true'` ya lo
 * excluye del árbol de accesibilidad real, pero `@testing-library/dom` resuelve la asociación
 * implícita de un `<label>` que envuelve su control por el `textContent` del propio `<label>` —
 * sin ese aislamiento, `getByLabelText('Idioma')` dejaría de encontrarlo porque el texto agregado
 * pasaría a ser «IdiomaES». Desde `sm:` el `<select>` recupera su ancho y su color de texto
 * (`sm:w-auto sm:text-ink`) y el `<span>` del código se oculta (`sm:hidden`): vuelve a verse
 * exactamente como antes de T-5b.
 */
export const LanguageSwitcher = (): React.JSX.Element => {
	const { t, i18n } = useTranslation();
	const idiomaActivo = (i18n.resolvedLanguage ?? 'es') as SupportedLocale;
	// `SUPPORTED_LOCALES` es un enumerado cerrado y corto (dos locales): un `Record` de llamadas
	// t() literales, en vez de `t(\`idioma.codigo.${idiomaActivo}\`)`, evita registrar un patrón
	// nuevo en `preservePatterns` de `i18next.config.ts` (fuera de la propiedad exclusiva de esta
	// tarea) — mismo molde que `etiquetaPorSello` en `cabecera.tsx` (`docs/lessons.md` § «Una
	// clave i18n indexada por variable no la ve el extractor, pero un Record de llamadas
	// literales sí»).
	const codigoPorIdioma: Record<SupportedLocale, string> = {
		en: t('idioma.codigo.en'),
		es: t('idioma.codigo.es'),
	};

	return (
		<span className='relative inline-flex items-center gap-2 text-sm'>
			<label className='inline-flex items-center gap-2'>
				<span className='sr-only text-gray-ink sm:not-sr-only'>{t('idioma.etiqueta')}</span>
				<select
					className={`${CLASE_BASE_SIN_TAMANO} min-h-11 w-12 px-2 text-transparent sm:w-auto sm:px-3 sm:text-ink`}
					onChange={evento => void i18n.changeLanguage(evento.target.value as SupportedLocale)}
					value={idiomaActivo}
				>
					{SUPPORTED_LOCALES.map(locale => (
						<option key={locale} value={locale}>
							{t(`idioma.${locale}`)}
						</option>
					))}
				</select>
			</label>
			<span
				aria-hidden='true'
				className='pointer-events-none absolute inset-y-0 left-2 flex items-center text-ink sm:hidden'
			>
				{codigoPorIdioma[idiomaActivo]}
			</span>
		</span>
	);
};
